'use client';
import './round-game.css';

import { PuzzleResultsState } from '@wt/app/game/_components/types';
import { GameImageElement, GameMapElement } from '@wt/app/game/_elements';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import { Coordinate } from '@wt/shared/components/maps/mapkit/types';
import { YearSlider } from '@wt/shared/components/yearslider';
import { MapInteractionEvent } from '@wt/shared/libs/mapkit';
import { isNil } from 'lodash';
import {
  ChangeEvent,
  Suspense,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { GameModeTag } from '../GameModeTag';
import { GuessButton } from '../GuessButton';
import { getSiteMode } from '@wt/utilities/siteMode';

export enum EDesktopGameView {
  image,
  map,
}

const SliderAndGuessButton = ({
  handleYearChange,
  year,
  handleGuess,
  animation,
  yearSelected,
  locationSelected,
  secondaryLocationSelected,
}: {
  handleYearChange: (e: ChangeEvent<HTMLInputElement>) => void;
  year: number | null;
  handleGuess: () => void;
  animation: string;
  yearSelected: boolean;
  locationSelected: boolean;
  secondaryLocationSelected: boolean;
}) => {
  return (
    <div className="flex flex-1 wheretaken:flex-none flex-col justify-between movies:justify-end">
      <div className="flex-1 wheretaken:hidden movies:hidden"></div>
      {getSiteMode() != 'wheretaken' && (
        <div className={`shrink py-3 ${animation}`}>
          <YearSlider
            onChange={handleYearChange}
            selectedYear={year}
            yearLabel={true}
          />
        </div>
      )}
      <div className="flex-1 wheretaken:hidden movies:hidden"></div>
      <div className={`shrink ${animation}`}>
        <GuessButton
          yearSelected={yearSelected}
          locationSelected={locationSelected}
          secondaryLocationSelected={secondaryLocationSelected}
          onClick={handleGuess}
        />
      </div>
    </div>
  );
};

export const RoundGameDesktop = ({
  results,
  selectedLocation,
  selectedLocationSecondary,
  handleLocation,
  handleLocationSecondary,
  year,
  handleYearChange,
  handleGuess,
}: {
  results: PuzzleResultsState;
  selectedLocation: Coordinate | undefined;
  selectedLocationSecondary: Coordinate | undefined;
  handleLocation: (e: MapInteractionEvent) => void;
  handleLocationSecondary: (e: MapInteractionEvent) => void;
  year: number | null;
  handleYearChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleGuess: () => void;
}) => {
  const [view, setView] = useState<EDesktopGameView>(EDesktopGameView.image);
  const [showAnimation, setShowAnimation] = useState<boolean>(false);
  const { gameMode } = useRoundProgress();

  const formRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);

  const loadeerRef = useRef<HTMLDivElement | null>(null);
  const imageLoaded = (
    e: SyntheticEvent<HTMLImageElement, Event> | undefined
  ) => {
    if (e) {
      /* */
      const target = imageContainerRef.current;
      if (target) {
        target.classList.add('game-image-loaded');
      }
    }

    const target = formRef.current;
    if (target instanceof HTMLDivElement) {
      target.classList.remove('opacity-0');
    }
    const loaderTarget = loadeerRef.current;
    if (loaderTarget instanceof HTMLDivElement) {
      loaderTarget.classList.add('hidden');
    }

    const imageTarget = imageRef.current;
    if (imageTarget instanceof HTMLDivElement) {
      imageTarget.classList.remove('opacity-0');
    }
  };

  useEffect(() => {
    imageLoaded(undefined);
  }, []);

  const toggleView = () => {
    setView(
      view === EDesktopGameView.image
        ? EDesktopGameView.map
        : EDesktopGameView.image
    );
    if (!showAnimation) {
      setShowAnimation(true);
    }
  };

  const layout = getSiteMode() == 'whentaken-movies' ? 'rows' : 'columns';

  const centeredContainerClass = 'mainpart flex';
  const centeredSubContainerClass = `flex grow ${showAnimation ? (view === EDesktopGameView.image ? 'animate-grow-bottom-right' : 'animate-grow-bottom-left') : ''}`;
  const centeredSubSubContainerClass = 'relative flex w-full flex-1 grow';

  const sideContainerClass = 'side h-auto';
  const sideSubContainerClass = `side-sub relative ${showAnimation ? (view === EDesktopGameView.image ? 'animate-shrink-top-left' : 'animate-shrink-top-right') : ''}`;
  const sideSubSubContainerClass = 'relative side-subsub w-full flex-1';

  return (
    <div className="flex size-full flex-col">
      {gameMode != 'daily' && (
        <div className="mx-auto mt-6 flex w-full max-w-screen-2xl justify-start px-8 desktopShort:max-w-5xl">
          <GameModeTag />
        </div>
      )}
      <div
        className={`page-body mx-auto max-w-screen-2xl py-6 desktopShort:max-w-5xl ${layout == 'rows' ? 'page-body-rows' : ''}`}
      >
        <div
          className={
            view === EDesktopGameView.image
              ? centeredContainerClass
              : sideContainerClass
          }
        >
          <div
            className={
              view === EDesktopGameView.image
                ? centeredSubContainerClass
                : sideSubContainerClass
            }
          >
            <div
              className={
                view === EDesktopGameView.image
                  ? centeredSubSubContainerClass
                  : sideSubSubContainerClass
              }
            >
              <GameImageElement
                results={results}
                puzzle={results.puzzle[results.round]}
                view={view}
                mode={'game'}
                onClickCenter={toggleView}
              />
            </div>
          </div>

          {view === EDesktopGameView.map && (
            <SliderAndGuessButton
              handleYearChange={handleYearChange}
              year={year}
              handleGuess={handleGuess}
              animation={showAnimation ? 'animate-slide-left' : ''}
              yearSelected={!isNil(year)}
              locationSelected={!isNil(selectedLocation)}
              secondaryLocationSelected={!isNil(selectedLocationSecondary)}
            />
          )}
        </div>

        <div
          className={
            view === EDesktopGameView.map
              ? centeredContainerClass
              : sideContainerClass
          }
        >
          <div
            className={
              view === EDesktopGameView.map
                ? centeredSubContainerClass
                : sideSubContainerClass
            }
          >
            <div
              className={
                view === EDesktopGameView.map
                  ? centeredSubSubContainerClass
                  : sideSubSubContainerClass
              }
            >
              <GameMapElement
                puzzle={results.puzzle[results.round]}
                selectedLocation={selectedLocation}
                selectedLocationSecondary={selectedLocationSecondary}
                onLocationSelect={handleLocation}
                onLocationSelectSecondary={handleLocationSecondary}
                isCentered={view === EDesktopGameView.map}
                onClickCenter={toggleView}
              />
            </div>
          </div>

          {view === EDesktopGameView.image && (
            <SliderAndGuessButton
              handleYearChange={handleYearChange}
              year={year}
              handleGuess={handleGuess}
              animation={showAnimation ? 'animate-slide-right' : ''}
              yearSelected={!isNil(year)}
              locationSelected={!isNil(selectedLocation)}
              secondaryLocationSelected={!isNil(selectedLocationSecondary)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
